import 'katex/dist/katex.min.css';
import { Dispatch, MutableRefObject, SetStateAction, useContext, useEffect, useMemo, useRef } from 'react';
import StarterKit from '@tiptap/starter-kit';
import Document from '@tiptap/extension-document';
import Underline from '@tiptap/extension-underline';
import { TaskItem, TaskList, ListItem, ListKeymap } from '@tiptap/extension-list';
import { ETiptapSelectorKey } from '@modules/documents/types/tiptap.types';
import TextAlign from '@tiptap/extension-text-align';
import Superscript from '@tiptap/extension-superscript';
import Subscript from '@tiptap/extension-subscript';
import Link from '@tiptap/extension-link';
import CustomTableHeader from '../tiptap/extensions/CustomTableHeader';
import { Placeholder } from '@tiptap/extensions';
import BulletListWithStyle from '../tiptap/extensions/BulletListWithStyle';
import InvisibleCharacters from '../tiptap/extensions/InvisibleCharacters';
import { Mathematics } from '../tiptap/extensions/Mathematics';

import HeadingNode, { TTreeHeading } from '../tiptap/nodes/heading/HeadingNode';
import { Indent } from '../tiptap/extensions/Indent';
import { ScrollLink } from '../tiptap/nodes/scrollLink/ScrollLink';
import { Editor, EditorOptions, UseEditorOptions } from '@tiptap/react';
import TableNode from '../tiptap/nodes/table/TableNode';
import TitleNode from '../tiptap/nodes/title/TitleNode';
import hasTitleNode from '../helpers/hasTitleNode.helper';
import { TITLE_NODE_NAME, TITLE_PLACEHOLDER } from '../tiptap/nodes/title/constants';
import ReferenceNode from '../tiptap/nodes/reference/ReferenceNode';
import LiteratureCitationNode from '../tiptap/nodes/literatureCitation/LiteratureCitationNode';
import { TDocumentConfig, TVersion, TDraft, TDocument } from '../types';
import { isTDraft } from '@modules/items';
import { SignInContext } from '@modules/users';
import getDocumentProductId from '../helpers/getDocumentProductId';
import resolveLiteratureDecision from '../helpers/resolveLiteratureDecision.helper';
import BlockNode from '../tiptap/nodes/block/BlockNode';
import InlineBlockNode from '../tiptap/nodes/inlineBlock/InlineBlockNode';
import CustomUniqueId, { addUniqueIdAttribute } from '../tiptap/extensions/CustomUniqueId';
import getTaskItemCheckboxSelectionPlugin from '../tiptap/plugins/getTaskItemCheckboxSelectionPlugin';
import PageBreak from '../tiptap/nodes/pageBreak/PageBreakNode';
import { SelectionMark, SelectionNode, unwrapSelectionFragment } from '@modules/comments';
import PasteRule from '../tiptap/extensions/PasteRule';
import BlockPasteWarning from '../tiptap/extensions/BlockPasteWarning';
import { UNSUPPORTED_BLOCK_PASTE_MESSAGE } from '../helpers/blockContentValidation.helper';
import ImageNode from '../tiptap/nodes/image/ImageNode';
import CaptionNode from '../tiptap/nodes/caption/CaptionNode';
import PulseHighlight from '../tiptap/extensions/PulseHighlight';
import CustomStyleMark from '../tiptap/extensions/CustomStyleMark';
import CustomStyledParagraph from '../tiptap/extensions/CustomStyledParagraph';
import { DEFAULT_DOCUMENT_CONTENT } from '../constants';
import CustomTableCell from '../tiptap/extensions/CustomTableCell';
import { TableRow } from '@tiptap/extension-table';
import { useDebouncedRef } from '@shared/hooks';
import { TCaptionMap } from '@shared/backendFrontend/captionHelpersFe';
import { TMap } from '@shared/types';
import { ToastContext } from '@shared/contexts';
import { TResolvedLiteratureCitation } from '../types/reference.types';
import OrderedListWithStart from '../tiptap/extensions/OrderedListWithStart';
import TabNode from '../tiptap/nodes/tab/TabNode';
import ElementRow from '../tiptap/nodes/elementRow/ElementRowNode';
import getTemplateFieldNodes from '../tiptap/nodes/templateField/getTemplateFieldNodes';
import getCopyRules from '../tiptap/nodes/templateField/getCopyRules';
import { usesFormFields } from '../helpers/formFields.helper';
import TemplateLocking from '../tiptap/extensions/TemplateLocking';
import AtomSelectionHighlight from '../tiptap/plugins/AtomSelectionHighlight';
import AiEditReviewPlugin from '../tiptap/plugins/AiEditReviewPlugin';
import ExplicitContentSelection from '../tiptap/plugins/ExplicitContentSelection';
import InitialContentMarker from '../tiptap/plugins/InitialContentMarker';
import { isVersionBound } from '../helpers/isVersionBound.helper';
import { headingSeed, headingTree, holdsDocumentStart, isSwapping, numberingSeed } from '../largeDocument';
import { OutRefBookmarkKind } from '../tiptap/nodes/caption/constants';
import { getResolveCaptionLabelFe } from '../../../shared/backendFrontend';

const useOptions = (
  doc: TDocument,
  isEdit: boolean,
  refEditor: MutableRefObject<Editor | null>,
  version: TVersion | TDraft | undefined,
  setTreeHeadings: Dispatch<SetStateAction<TTreeHeading[]>>,
  setCaptionsMap: Dispatch<SetStateAction<TCaptionMap | undefined>>,
  setIsTableMenuOpen: Dispatch<SetStateAction<boolean>>,
  setIsImageMenuOpen: Dispatch<SetStateAction<boolean>>,
  templateDropdownOptionsRef: MutableRefObject<Record<string, string[]>>
): {
  options: Partial<EditorOptions>;
  refTreeHeadings: MutableRefObject<TTreeHeading[]>;
  resolveCaptionLabel: (kind: OutRefBookmarkKind) => string;
} => {
  /**
   * IMPORTANT: use `useRef` when passing vars into Tiptap extensions/nodes to avoid stale values.
   * However, it's not needed when passing setters from `useState`, as they are stable.
   */
  const refConfig = useRef<TDocumentConfig | undefined>();
  refConfig.current = version?.config;
  const refIsEdit = useRef(isEdit);
  refIsEdit.current = isEdit;

  /* ─── headings ─────────────────────────────────────────────── */
  const { ref: refTreeHeadings, onUpdate: onUpdateHeadings } = useDebouncedRef<TTreeHeading[]>(setTreeHeadings, []);

  /* ─── captions ─────────────────────────────────────────────── */
  const { ref: _refCaptionsMap, onUpdate: onUpdateCaptions } = useDebouncedRef<TCaptionMap | undefined>(
    setCaptionsMap,
    {}
  );

  /* ─── caption labels i18n ──────────────────────────────────── */
  const resolveCaptionLabel = useMemo(() => getResolveCaptionLabelFe(version), [version]);
  useEffect(() => {
    // trigger caption labels update when language custom-field changes
    refEditor.current?.commands?.bumpCaptionLabelNonce?.(); // conditional chaining for safety
  }, [resolveCaptionLabel]);
  const refResolveCaptionLabel = useRef(resolveCaptionLabel);
  refResolveCaptionLabel.current = resolveCaptionLabel;

  /* ─── literature citation status ────────────────────────────── */
  const { organization } = useContext(SignInContext);
  const { show } = useContext(ToastContext);
  // Scalar deps only - listing `doc` rebuilds the memo on every unrelated field refetch and fires the nonce bump for nothing.
  const productId = getDocumentProductId(doc, organization);
  const frozenCitations = version && !isTDraft(version) ? version.literatureCitations : undefined;
  // Version views swap live axes for frozen ones so the chip reflects the regulatory snapshot.
  // QM/country-reg docs skip the decision axis - storage preserves raw truth (undefined), view reports NOT_REQUIRED here.
  const literatureStatusData = useMemo<TMap<string, TResolvedLiteratureCitation>>(() => {
    const live = doc.resolvedLiteratureCitations ?? {};
    if (!frozenCitations) return live;
    const frozenById = new Map(frozenCitations.map((c) => [c.itemId, c]));
    const out: TMap<string, TResolvedLiteratureCitation> = {};
    for (const [litId, entry] of Object.entries(live)) {
      const frozen = frozenById.get(litId);
      if (!frozen) {
        out[litId] = entry;
        continue;
      }
      const frozenDecision = resolveLiteratureDecision(!!productId, frozen.decision ?? null);
      out[litId] = { ...entry, decision: frozenDecision, archived: frozen.archived };
    }
    return out;
  }, [doc.resolvedLiteratureCitations, frozenCitations, productId]);

  const literatureStatusDataRef = useRef<TMap<string, TResolvedLiteratureCitation>>(literatureStatusData);
  useEffect(() => {
    literatureStatusDataRef.current = literatureStatusData;
    refEditor.current?.commands?.bumpLiteratureCitationStatusNonce?.();
  }, [literatureStatusData]);

  const shouldLock = !doc.isTemplate && isVersionBound(version);
  const isFormFieldEditor = usesFormFields(doc);

  const options = useMemo(() => {
    const { idAttributes, copyFields } = getCopyRules(templateDropdownOptionsRef, isFormFieldEditor);

    const options: Partial<UseEditorOptions> = {
      editable: false, // Start non-editable; toggled via editor.setEditable()
      shouldRerenderOnTransaction: false,

      extensions: [
        StarterKit.configure({
          paragraph: false,
          document: false,
          bulletList: false,
          orderedList: false,
          listItem: false,
          heading: false,
          horizontalRule: false,
          // Out of the schema entirely, so they cannot be typed, pasted or commanded into a document.
          blockquote: false,
          codeBlock: false,
          code: false,
          link: false,
          underline: false,
          // Not adopted: useEditorContentClick adds a trailing paragraph on click-below, so no persisted trailing node is needed.
          trailingNode: false,
          // StarterKit's built-in listKeymap targets 'orderedList'; we register our own below for orderedListWithStart.
          listKeymap: false,
        }),

        addUniqueIdAttribute(CustomStyledParagraph),
        Underline,
        PulseHighlight.configure({ multicolor: true }),
        CustomStyleMark,
        SelectionMark,
        SelectionNode,
        TextAlign.configure({
          types: ['paragraph'],
        }),
        Superscript,
        Subscript,

        addUniqueIdAttribute(
          BulletListWithStyle.configure({
            keepMarks: true,
          })
        ),
        addUniqueIdAttribute(OrderedListWithStart.configure({ keepMarks: true })),
        addUniqueIdAttribute(ListItem.configure({ orderedListTypeName: ETiptapSelectorKey.ORDERED_LIST_WITH_START })),
        addUniqueIdAttribute(TaskList),
        addUniqueIdAttribute(
          TaskItem.extend({
            addProseMirrorPlugins() {
              return [...(this.parent?.() ?? []), getTaskItemCheckboxSelectionPlugin()];
            },
          }).configure({
            nested: true,
          })
        ),

        // Word-like Backspace/Delete at list boundaries; configured for our renamed orderedListWithStart wrapper.
        ListKeymap.configure({
          listTypes: [
            {
              itemName: ETiptapSelectorKey.LIST_ITEM,
              wrapperNames: [ETiptapSelectorKey.BULLET_LIST, ETiptapSelectorKey.ORDERED_LIST_WITH_START],
            },
            { itemName: ETiptapSelectorKey.TASK_ITEM, wrapperNames: [ETiptapSelectorKey.TASK_LIST] },
          ],
        }),

        // normal external-link:
        Link.extend({ inclusive: false }).configure({ openOnClick: false }),
        // scroll-link mark:
        ScrollLink,
        addUniqueIdAttribute(ImageNode.configure({ setIsImageMenuOpen })),
        addUniqueIdAttribute(ElementRow),
        Indent, // must be placed before Table extension so the tab key triggers the table extension instead of the indent extension
        addUniqueIdAttribute(
          TableNode.configure({
            allowTableNodeSelection: true,
            setIsTableMenuOpen,
          })
        ),
        addUniqueIdAttribute(TableRow),
        addUniqueIdAttribute(CustomTableHeader),
        addUniqueIdAttribute(CustomTableCell),

        addUniqueIdAttribute(PageBreak),
        addUniqueIdAttribute(TabNode.configure({ isEditMode: () => refIsEdit.current })),

        InvisibleCharacters.configure({
          // start hidden; let users toggle on demand
          visible: false,
        }),

        Mathematics.configure({
          regex: new RegExp(/\\\(([\s\S]+?)\\\)/g), // must match backend LaTeX delimiter
          katexOptions: {
            trust: false,
          },
          shouldRender: (_state, _pos, node) => node.type.name === 'text',
        }),

        AtomSelectionHighlight,
        ExplicitContentSelection,
        InitialContentMarker,
        AiEditReviewPlugin,
      ],
      editorProps: {
        attributes: {
          class: 'focus:outline-none notranslate',
          translate: 'no',
        },
        // Mid-swap the editor is out of flow and invisible. ProseMirror scrolling its selection into
        // view would yank the page, and a keystroke reaching it would be overwritten by the incoming
        // setContent - or, undone, would revert the part replacement itself.
        handleScrollToSelection: () => isSwapping(),
        handleKeyDown: () => isSwapping(),
      },
      /**
       * In the title, where a mandatory title used to put it.
       *
       * The title became optional so that a part other than the first is not given a synthesized one.
       * A side effect was that this bare string became a paragraph instead, so the editor showed small
       * body text under an "Untitled" placeholder rather than the large title it always had. Editors
       * without a title node keep the bare string.
       */
      content: hasTitleNode(doc) ? `<title-node>${DEFAULT_DOCUMENT_CONTENT}</title-node>` : DEFAULT_DOCUMENT_CONTENT,
    };

    if (doc.isBlock)
      options.extensions = options.extensions?.concat([
        Document,
        BlockPasteWarning.configure({
          onUnsupportedPaste: () => show('warning', UNSUPPORTED_BLOCK_PASTE_MESSAGE, { timeout: 10000 }),
        }),
      ]);
    else
      options.extensions = options.extensions?.concat([
        ...(hasTitleNode(doc)
          ? [
              addUniqueIdAttribute(TitleNode.extend({ priority: 1 }).configure({ holdsDocumentStart })),
              Document.extend({
                // Optional title only when documents load in parts: a part other than the first does
                // not contain the title, and a mandatory title makes ProseMirror synthesize one -
                // which a commit would then splice into the middle of the document. The model
                // guarantees element 0 IS a title, so the first part still has one to show, and
                // `getRequiredTitlePlugin` keeps one there.
                content: 'title? (rootOnly | block)*',
              }),
            ]
          : doc.withoutTitle
            ? // Without a title, sections stay at the root; block first, so an emptied root gets a paragraph.
              [Document.extend({ content: '(block | rootOnly)+' })]
            : [Document]),
        // The three numbering paths take their whole-document input from the large-document model
        // here, and only here. Each answers null when the whole document is in the editor, and the
        // plugin then numbers exactly as it did before.
        addUniqueIdAttribute(
          HeadingNode.configure({
            levels: [1, 2, 3, 4, 5],
            refEditor,
            refConfig,
            onUpdate: onUpdateHeadings,
            headingSeed,
            headingTree,
          })
        ),
        addUniqueIdAttribute(ReferenceNode),
        // LiteratureCitationNode: no per-insertion UUID → NOT in UNIQUE_ID_NODE_TYPES.
        LiteratureCitationNode.configure({ getStatusDataRef: () => literatureStatusDataRef, numberingSeed }),
        addUniqueIdAttribute(
          CaptionNode.configure({
            onUpdate: onUpdateCaptions,
            refResolveLabel: refResolveCaptionLabel,
            numberingSeed,
          })
        ),
        addUniqueIdAttribute(BlockNode),
        addUniqueIdAttribute(InlineBlockNode),
        ...getTemplateFieldNodes({
          usesFormFields: isFormFieldEditor,
          isTemplate: doc.isTemplate,
          optionsRef: templateDropdownOptionsRef,
          copyFields,
        }),

        // Lock template structure in bound documents (only field content is editable)
        ...(shouldLock ? [TemplateLocking] : []),

        Placeholder.configure({
          showOnlyCurrent: false,
          showOnlyWhenEditable: false,
          placeholder: ({ node }) => {
            return node.type.name === TITLE_NODE_NAME ? TITLE_PLACEHOLDER : '';
          },
        }),
      ]);

    // CustomUniqueId must be last to ensure its ID fixes aren't overwritten by other plugins
    options.extensions = options.extensions?.concat([
      CustomUniqueId.configure({ idAttributes }),

      PasteRule.configure({
        transformFragments: [unwrapSelectionFragment, copyFields],
      }),
    ]);
    return options;
  }, [doc.isBlock, shouldLock, isFormFieldEditor]);

  return useMemo(() => ({ options, refTreeHeadings, resolveCaptionLabel }), [options, resolveCaptionLabel]);
};

export default useOptions;
